// delete this entire file when cleaning up isVisualRefreshEnabled() FG
/** @jsx jsx */
import React, { Component, createRef } from 'react';
import { jsx } from '@compiled/react';
import flow from 'lodash/flow';
import noop from 'lodash/noop';
import { IconButton } from '@atlaskit/button/new';
import { cssMap } from '@atlaskit/css';
import DropdownMenu, { DropdownItemGroup, DropdownItem } from '@atlaskit/dropdown-menu';
import MoreIcon from '@atlaskit/icon/core/show-more-horizontal';
import { token } from '@atlaskit/tokens';
import withFireUiAnalytics from '@atlassian/jira-analytics-web-react/src/components/with-fire-ui-analytics.tsx';
import ComponentWithAnalytics from '@atlassian/jira-analytics-web-react/src/utils/component-with-analytics.tsx';
import type { AnalyticsEvent } from '@atlassian/jira-common-analytics-v2-wrapped-components/src/types.tsx';
import type { User } from '@atlassian/jira-common-directory-v2/src/model/index.tsx';
import { injectIntl } from '@atlassian/jira-intl';
import type { Intl } from '@atlassian/jira-shared-types/src/general.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import { useModalDialogActions } from '@atlassian/jira-software-modal-dialog/src/controllers/index.tsx';
import type { ReturnFocusTo } from '@atlassian/jira-software-modal-dialog/src/common/types.tsx';
import { mergeRefs } from '@atlassian/jira-merge-refs/src/index.tsx';
import messages from './messages.tsx';
import { UpdateFilterModal } from './update-filter-modal/index.tsx';

const styles = cssMap({
	container: {
		display: 'flex',
		justifyContent: 'flex-end',
		paddingTop: token('space.050'),
		paddingRight: token('space.025'),
		paddingBottom: token('space.0'),
		paddingLeft: token('space.0'),
	},
	spinnerContainer: {
		height: '16px',
		width: '24px',
	},
});

type Props = {
	isCopyFilterModalOpen: boolean;
	isWritable: boolean;
	defaultOpen: boolean;
} & Intl & {
		showSpinner: boolean;
		manageSubscriptionsUrl: string;
		atlassianAccountId: string;
		name: string | undefined;
		owner: User | null;
		isAdmin: boolean;
		filterId: string;
		onCopy: () => void;
		onChangeOwner: () => void;
		onEdit: () => void;
		onDelete: () => void;
		onEditFilterClick: (arg1: AnalyticsEvent) => void;
		onChangeOwnerFilterClick: (arg1: AnalyticsEvent) => void;
		onDeleteFilterClick: (arg1: AnalyticsEvent) => void;
		onManageSubscriptionsClick: (arg1: AnalyticsEvent) => void;
		onCopyFilterClick: (arg1: AnalyticsEvent) => void;
		onOpenCopyFilterModal: () => void;
		setReturnFocusTo: (returnFocusTo: ReturnFocusTo | null) => void | Promise<void>;
	};

type State = {
	isOpen: boolean;
	isCopyFilterModalOpen: boolean;
};

const DropdownMenuWithAnalytics = ComponentWithAnalytics('dropdownMenu', {
	onOpenChange: 'open',
})(DropdownMenu);

const DropdownItemWithAnalytics = ComponentWithAnalytics('dropdownItem', {
	onClick: 'clicked',
})(DropdownItem);

// eslint-disable-next-line jira/react/no-class-components
class ActionsOld extends Component<Props, State> {
	static defaultProps = {
		isWritable: false,
		defaultOpen: false,
		showSpinner: false,
		manageSubscriptionsUrl: '',
		name: undefined,
		owner: null,
		filterId: '',
		onCopy: noop,
		onChangeOwner: noop,
		onEdit: noop,
		onDelete: noop,
		setReturnFocusTo: noop,
	};

	state = {
		isOpen: this.props.defaultOpen,
		isCopyFilterModalOpen: false,
	};

	componentDidUpdate(prevProps: Props) {
		if (
			prevProps.isCopyFilterModalOpen &&
			this.state.isCopyFilterModalOpen &&
			!this.props.isCopyFilterModalOpen
		) {
			this.setState({ isCopyFilterModalOpen: false }, () => {
				if (this.triggerButtonRef && this.triggerButtonRef.current) {
					this.triggerButtonRef.current.focus();
				}
			});
		}
	}

	triggerButtonRef = createRef<HTMLButtonElement>();

	// @ts-expect-error - TS7031 - Binding element 'isOpen' implicitly has an 'any' type.
	onOpenChange = ({ isOpen }) => {
		this.setState(() => ({ isOpen }));
	};

	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	onCopyFilterClick = (e: any, analytics: any) => {
		this.props.onCopyFilterClick(analytics);
		this.props.onCopy();
		this.props.onOpenCopyFilterModal();
		this.setState(() => ({ isCopyFilterModalOpen: true }));
	};

	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	onManageSubscriptionsClick = (e: any, analytics: any) => {
		this.props.onManageSubscriptionsClick(analytics);
		this.delayedClose();
	};

	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	onEditFilterClick = (e: any, analytics: any) => {
		this.props.onEditFilterClick(analytics);
		this.props.onEdit();
		this.triggerButtonRef.current && this.props.setReturnFocusTo(this.triggerButtonRef);
	};

	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	onChangeOwnerFilterClick = (e: any, analytics: any) => {
		this.props.onChangeOwnerFilterClick(analytics);
		this.props.onChangeOwner();
		if (fg('jfp_a11y_fix_filters_change_owner_modal_focus')) {
			this.triggerButtonRef.current && this.props.setReturnFocusTo(this.triggerButtonRef);
		}
	};

	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	onDeleteFilterClick = (e: any, analytics: any) => {
		this.props.onDeleteFilterClick(analytics);
		this.props.onDelete();
		this.triggerButtonRef.current && this.props.setReturnFocusTo(this.triggerButtonRef);
	};

	/**
	 * Close the menu with a minimal delay so the inner handlers are able to react on keypress properly.
	 * In its current implementation if we close the menu right away it won't open the focused `href`.
	 * Ideally we have to get right of this and few more methods when the bug linked below is fixed.
	 * @see https://ecosystem.atlassian.net/browse/AK-4467
	 */
	delayedClose() {
		setTimeout(() => this.setState(() => ({ isOpen: false })));
	}

	render() {
		const {
			showSpinner,
			intl: { formatMessage },
			manageSubscriptionsUrl,
			atlassianAccountId,
			name,
			owner,
			isWritable,
			isAdmin,
			filterId,
		} = this.props;

		const isOwner = owner && owner.accountId === atlassianAccountId;

		const { isOpen } = this.state;

		// @ts-expect-error - TS7031 - Binding element 'triggerRef' implicitly has an 'any' type.
		const triggerFunction = ({ triggerRef, ...props }) => (
			<IconButton
				{...props}
				appearance="subtle"
				icon={MoreIcon}
				ref={mergeRefs(triggerRef, this.triggerButtonRef)}
				isLoading={showSpinner}
				label={
					name ? formatMessage(messages.tooltipWithName, { name }) : formatMessage(messages.tooltip)
				}
			/>
		);

		return (
			<div css={styles.container}>
				<DropdownMenuWithAnalytics
					// @ts-expect-error - Type '{ children: Element; position: string; isOpen: boolean; onOpenChange: ({ isOpen }: { isOpen: any; }) => void; trigger: ({ triggerRef, ...props }: { [x: string]: any; triggerRef: any; }) => Element; }' is not assignable to type 'IntrinsicAttributes & Omit<{ appearance?: "default" | "tall" | undefined; autoFocus?: boolean | undefined; children?: ReactNode; isLoading?: boolean | undefined; ... 11 more ...; label?: string | undefined; }, keyof WithAnalyticsEventsProps>'.
					position="bottom right"
					isOpen={isOpen}
					onOpenChange={this.onOpenChange}
					trigger={triggerFunction}
					shouldRenderToParent
				>
					<DropdownItemGroup>
						<DropdownItemWithAnalytics
							href={manageSubscriptionsUrl}
							onClick={this.onManageSubscriptionsClick}
						>
							{formatMessage(messages.manageAction)}
						</DropdownItemWithAnalytics>
						<DropdownItemWithAnalytics onClick={this.onCopyFilterClick}>
							{formatMessage(messages.copyAction)}
						</DropdownItemWithAnalytics>
						{(isWritable || isOwner === true) &&
							(fg('nin-save-filter-dialog-entry-point') ? (
								<UpdateFilterModal filterId={filterId} returnFocusRef={this.triggerButtonRef} />
							) : (
								<DropdownItemWithAnalytics onClick={this.onEditFilterClick}>
									{formatMessage(messages.editFilter)}
								</DropdownItemWithAnalytics>
							))}
						{(isOwner === true || isAdmin === true) && (
							<>
								<DropdownItemWithAnalytics onClick={this.onChangeOwnerFilterClick}>
									{formatMessage(messages.changeOwner)}
								</DropdownItemWithAnalytics>
								<DropdownItemWithAnalytics onClick={this.onDeleteFilterClick}>
									{formatMessage(messages.deleteFilter)}
								</DropdownItemWithAnalytics>
							</>
						)}
					</DropdownItemGroup>
				</DropdownMenuWithAnalytics>
			</div>
		);
	}
}

const ActionsWithReturnToFocus = (props: Omit<Props, 'setReturnFocusTo'>) => {
	const { setReturnFocusTo } = useModalDialogActions();
	return <ActionsOld {...props} setReturnFocusTo={setReturnFocusTo} />;
};

export default flow(
	injectIntl,
	withFireUiAnalytics({
		onManageSubscriptionsClick: 'manageSubscriptions',
		onCopyFilterClick: 'copy',
		onChangeOwnerFilterClick: 'changeOwner',
		onEditFilterClick: 'edit',
		onDeleteFilterClick: 'delete',
	}),
)(ActionsWithReturnToFocus);
